import { useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * 사용 공통 모듈 패널 (§0.4 / R7-059).
 *
 * spec §0.1 의 usesCommon 목록을 접기/펼치기 테이블로 시각화한다.
 * SystemSettingsPage · LogoutInspectorPage 와 동일 패턴.
 */
export function CommonUsagePanel() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const modules = [
    { id: 'C001', name: 'BusinessException / NotFoundException / ConflictException', role: '도메인 예외 throw (USER-404/409/403/422)' },
    { id: 'C002', name: 'ApiResponse<T> · PageResponse<T>', role: '모든 응답 래핑 및 목록 페이지 응답' },
    { id: 'C003', name: '@Email · @NoXss · ValidationFacade', role: 'CreateUserRequest / UpdateUserRequest 입력 검증' },
    { id: 'C006', name: 'RequestContext.userId()', role: '자기 자신 ROLE_ADMIN 박탈/삭제/잠금 차단 (USER-403-001/002)' },
    { id: 'C007', name: 'CommonCodeService.findValues("USER_TYPE")', role: '역할 변경 UI 셀렉트박스 데이터' },
    { id: 'C008', name: 'EventPublisher.publish(BizEvent)', role: 'UserCreatedEvent · UserRoleChangedEvent · UserLockedEvent · UserUnlockedEvent 발행' },
    { id: 'C014', name: '@Audited(action, resource)', role: '모든 mutation 에 감사 로그 자동 기록 (AOP)' },
    { id: 'C028', name: 'BCryptPasswordEncoder + JwtAuthenticationService.revokeAllForUser()', role: '임시 비밀번호 해시 / 잠금·삭제·역할변경 시 활성 토큰 일괄 폐기' },
    { id: 'C029', name: 'SecurityChainCustomizer', role: '/api/eraf/users/** 인증 필수 정책' },
    { id: 'C030', name: '@HasPermission("USER_READ" / "USER_WRITE")', role: '메서드 단위 권한 이중 방어 (DynamicPermissionService)' },
    { id: 'C037', name: '@ApiSuccessResponse · @ApiErrorResponses', role: '8개 endpoint OpenAPI 문서화' },
    { id: 'C041', name: 'SpecificationBuilder · FieldWhitelist · SortWhitelist', role: '목록 검색/정렬 동적 쿼리 (SQL injection 방어)' },
    { id: 'C042', name: 'GenericRepository<UserAccount, Long>', role: 'UserAccountRepository 기반 — login biz 소유, user-management 가 read-write 접근' },
    { id: 'C078', name: 'ExcelExporter<UserExportDto> · @ExcelColumn', role: '사용자 목록 xlsx 다운로드 (SXSSF streaming)' },
  ];

  return (
    <div
      style={{
        marginTop: 'var(--spacing-6)',
        border: '1px solid var(--color-surface-border)',
        borderRadius: 'var(--radius-md)',
        background: 'var(--color-surface-muted)',
      }}
    >
      <button
        type="button"
        aria-expanded={open}
        aria-controls="user-common-usage-panel"
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--spacing-2)',
          width: '100%',
          padding: 'var(--spacing-3) var(--spacing-4)',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          fontSize: 'var(--font-size-sm)',
          color: 'var(--color-text-secondary)',
          fontFamily: 'var(--font-family-sans)',
          textAlign: 'left',
        }}
      >
        <span
          style={{
            transition: 'transform 0.2s',
            transform: open ? 'rotate(180deg)' : 'none',
            display: 'inline-block',
          }}
        >
          ▾
        </span>
        {t('user.commonUsage.title')} ({t('user.commonUsage.itemCount', { count: modules.length })})
      </button>

      {open && (
        <div
          id="user-common-usage-panel"
          style={{
            padding: 'var(--spacing-4)',
            borderTop: '1px solid var(--color-surface-border)',
          }}
        >
          <p
            style={{
              margin: '0 0 var(--spacing-3)',
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-text-muted)',
            }}
          >
            {t('user.commonUsage.description')}
          </p>
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: 'var(--font-size-xs)',
            }}
          >
            <thead>
              <tr>
                {[
                  t('user.commonUsage.col.module'),
                  t('user.commonUsage.col.api'),
                  t('user.commonUsage.col.role'),
                ].map((h) => (
                  <th
                    key={h}
                    style={{
                      textAlign: 'left',
                      padding: 'var(--spacing-2)',
                      borderBottom: '1px solid var(--color-surface-border)',
                      color: 'var(--color-text-secondary)',
                      fontWeight: 600,
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {modules.map((m) => (
                <tr key={m.id}>
                  <td
                    style={{
                      padding: 'var(--spacing-2)',
                      borderBottom: '1px solid var(--color-surface-border)',
                      fontFamily: 'var(--font-family-mono)',
                      color: 'var(--color-brand-primary)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {m.id}
                  </td>
                  <td
                    style={{
                      padding: 'var(--spacing-2)',
                      borderBottom: '1px solid var(--color-surface-border)',
                      fontFamily: 'var(--font-family-mono)',
                      color: 'var(--color-text-primary)',
                    }}
                  >
                    {m.name}
                  </td>
                  <td
                    style={{
                      padding: 'var(--spacing-2)',
                      borderBottom: '1px solid var(--color-surface-border)',
                      color: 'var(--color-text-secondary)',
                    }}
                  >
                    {m.role}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
